import { use, useCallback } from 'react';
import { isDesktop } from 'react-device-detect';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { OrgType } from '../../../common/src/model.ts';
import { getNameForOrgType } from '../../../common/src/model_util.ts';
import { isSingingPlanForDate } from '../../../common/src/org.ts';
import { Pages } from '../../../common/src/pages.ts';
import { ensureUnreachable } from '../../../common/src/util.ts';
import CalendarIcon from '../assets/calendar-outlined.svg?react';
import ChurchIcon from '../assets/church.svg?react';
import OutlinedHeartIcon from '../assets/heart-outline.svg?react';
import LearnBullseyeIcon from '../assets/learn-bullseye.svg?react';
import LibraryBooks from '../assets/library-books.svg?react';
import SchoolIcon from '../assets/school.svg?react';
import SmallGroupIcon from '../assets/small-group.svg?react';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { useCurrentOrg, useOrgEventsViewed } from '../data/use_current_org.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { MiniPlayer, useIsMiniPlayerVisible } from '../player/mini_player.tsx';
import { getPageFromLocation, getUrlForPage } from '../util/path.ts';
import { SearchContext } from '../util/search_control.tsx';
import { RedDotIcon } from '../util/shared.tsx';
import { useCurrentDate } from '../util/use_current_date.tsx';
import './footer.css';

const stickyPages = [Pages.Today, Pages.Organization, Pages.Favorites, Pages.Library, Pages.Learn];

export const Footer = () => {
  // TODO(hewitt): Should be using <NavLink> to style currently active tab
  const isMiniPlayerVisible = useIsMiniPlayerVisible();
  return (
    <FooterStacker>
      <MiniPlayer />
      <FooterWrapper $isDesktop={isDesktop} $isMiniPlayerVisible={isMiniPlayerVisible}>
        {/* Learn sits at the far right, past Library (issue #3518) */}
        <TodayTab key="today" />
        <OrgTab key="org" />
        <FooterTab key="favorites" forPage={Pages.Favorites} title="Favorites" IconElementType={OutlinedHeartIcon} />
        <FooterTab key="library" forPage={Pages.Library} title="Library" IconElementType={LibraryBooks} />
        <LearnTab key="learn" />
      </FooterWrapper>
    </FooterStacker>
  );
};

function FooterTab({
  forPage,
  title,
  isRedDotVisible,
  IconElementType,
}: {
  forPage: Pages;
  title: string;
  isRedDotVisible?: boolean;
  IconElementType: any;
}) {
  const { currentOrg } = useCurrentOrg();
  const ThisWeekIconClass = getIconForOrgType(currentOrg?.type);
  const location = useLocation();
  const [_, setMostRecentFooterTab] = useLocalStorage(LocalStorageKey.MostRecentFooterTab);
  const page = getPageFromLocation(location);

  const { setSearchString } = use(SearchContext);
  const clearSearch = useCallback(() => {
    setSearchString('');
    if (stickyPages.includes(forPage)) {
      setMostRecentFooterTab(forPage);
    }
  }, [forPage, setMostRecentFooterTab, setSearchString]);

  function getIconClassName() {
    let sizeClassName: string;
    switch (forPage) {
      case Pages.Today:
        sizeClassName = 'thisWeekIcon';
        break;
      case Pages.Organization: // organization page (church / school / small group)
        sizeClassName = 'organizationIcon'; // TODO(hewitt): should be church / school / group icon
        break;
      case Pages.Library:
        sizeClassName = 'libraryIcon';
        break;
      case Pages.Favorites:
        sizeClassName = 'favoritesIcon';
        break;
      case Pages.Learn:
        sizeClassName = 'learnIcon';
        break;
      case Pages.Help:
        sizeClassName = 'helpIcon';
        break;
      default:
        sizeClassName = '';
        break;
    }

    if (forPage === Pages.Organization && ThisWeekIconClass === ChurchIcon) {
      sizeClassName += ' churchIcon';
    }

    return sizeClassName + ' footerIcon ' + (forPage === page ? 'selectedIcon' : 'unselectedIcon');
  }

  function getLabelClassName() {
    return forPage === page ? 'selectedLabel iconLabel' : 'unselectedLabel iconLabel';
  }
  const content = (
    <>
      <IconWrapper>
        {<IconElementType className={getIconClassName()} />}
        {isRedDotVisible && <RedDotIcon />}
      </IconWrapper>
      <div className={getLabelClassName()}>{title}</div>
    </>
  );

  // TODO(hewitt): Should be using <NavLink> to style currently active tab
  return (
    <Link id={getFooterTabId(forPage)} className="iconDiv" to={getUrlForPage(forPage)} onClick={clearSearch}>
      {content}
    </Link>
  );
}

export function getFooterTabId(page: Pages) {
  return `footer-tab-${page}`;
}

function TodayTab() {
  const { householdOrgs } = useCurrentOrg();
  const [mostRecentTodayTabVisit] = useLocalStorage(LocalStorageKey.MostRecentTodayTabVisit);
  const today = useCurrentDate();
  const alreadyVisitedTodayTab = mostRecentTodayTabVisit && mostRecentTodayTabVisit >= today;
  const isRedDotVisible = isSingingPlanForDate(householdOrgs, today) && !alreadyVisitedTodayTab;

  return (
    <FooterTab forPage={Pages.Today} title="Today" isRedDotVisible={isRedDotVisible} IconElementType={CalendarIcon} />
  );
}

function OrgTab() {
  const { currentOrg } = useCurrentOrg();
  const ThisWeekIconClass = getIconForOrgType(currentOrg?.type);
  const { eventUpdatesViewed } = useOrgEventsViewed();
  return (
    <FooterTab
      forPage={Pages.Organization}
      title={getNameForOrgType(currentOrg?.type)}
      isRedDotVisible={!eventUpdatesViewed}
      IconElementType={ThisWeekIconClass}
    />
  );
}

// Every client: a store app older than 24.0 gets an update screen under it (learn_visibility.ts, #3821)
function LearnTab() {
  return <FooterTab forPage={Pages.Learn} title="Learn" IconElementType={LearnBullseyeIcon} />;
}

export function getIconForOrgType(type?: OrgType): any {
  if (!type) {
    return ChurchIcon;
  }
  switch (type) {
    case OrgType.School:
      return SchoolIcon;
    case OrgType.Church:
      return ChurchIcon;
    case OrgType.SmallGroup:
      return SmallGroupIcon;
    default:
      ensureUnreachable(type);
  }
}

const FooterStacker = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FooterWrapper = styled.div<{ $isDesktop: boolean; $isMiniPlayerVisible: boolean }>`
  display: flex;
  flex-basis: auto;
  width: 100%;
  background-color: var(--color-background);
  padding-bottom: ${(props) => (props.$isDesktop ? '0' : '20')};
  ${(props) => (props.$isMiniPlayerVisible ? '' : 'border-top: 1px solid var(--color-hairline)')};
`;

const IconWrapper = styled.div`
  position: relative;
`;
